import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';

type TopLevelSectionId =
  | 'get-api-key'
  | 'documents'
  | 'templates'
  | 'sender-identities'
  | 'brands'
  | 'webhooks';
type DocumentSubSectionId =
  | 'create-document'
  | 'update-document'
  | 'document-details-status'
  | 'send-reminders'
  | 'revoke-document'
  | 'send-document-from-template'
  | 'download-document'
  | 'delete-document'
  | 'embedded-configure-fields'
  | 'refresh-configure-url';
type TemplateSubSectionId =
  | 'create-template'
  | 'update-template'
  | 'template-details'
  | 'delete-template'
  | 'embedded-template-configure-fields'
  | 'refresh-template-configure-url';
type SenderIdentitySubSectionId =
  | 'create-sender-identity'
  | 'update-sender-identity'
  | 'resend-sender-identity-invitation'
  | 'rerequest-sender-identity-approval'
  | 'delete-sender-identity';
type BrandSubSectionId = 'list-brands' | 'create-brand' | 'update-brand' | 'delete-brand';
type WebhookSubSectionId = 'webhooks-overview' | 'document-events' | 'sender-identity-events' | 'template-events';

interface ApiKeyGuideStep {
  title: string;
  detail: string;
}

interface ApiKeyGuideNote {
  title: string;
  detail: string;
}

interface GuideListItem {
  title: string;
  detail: string;
}

interface GuideFieldItem {
  field: string;
  status: 'Required' | 'Optional' | 'Auto-generated';
  detail: string;
}

interface GuideMetaItem {
  label: string;
  value: string;
}

interface PageSectionLink {
  label: string;
  targetId: string;
}

interface WebhookEventDoc {
  event: string;
  meaning: string;
  subject: string;
  notes?: string;
  payload: string;
}

@Component({
  selector: 'app-api-key-guide',
  imports: [CommonModule, RouterLink],
  templateUrl: './api-key-guide.component.html',
  styleUrl: './api-key-guide.component.scss',
})
export class ApiKeyGuideComponent {
  private readonly documentRef = inject(DOCUMENT);

  activeSection: TopLevelSectionId = 'get-api-key';
  documentsExpanded = false;
  templatesExpanded = false;
  senderIdentitiesExpanded = false;
  brandsExpanded = false;
  webhooksExpanded = false;
  activeDocumentSubSection: DocumentSubSectionId = 'create-document';
  activeTemplateSubSection: TemplateSubSectionId = 'create-template';
  activeSenderIdentitySubSection: SenderIdentitySubSectionId = 'create-sender-identity';
  activeBrandSubSection: BrandSubSectionId = 'list-brands';
  activeWebhookSubSection: WebhookSubSectionId = 'document-events';
  activePageSectionId = 'api-key-overview';

  readonly apiKeySections: PageSectionLink[] = [
    { label: 'Overview', targetId: 'api-key-overview' },
    { label: 'Prerequisites', targetId: 'prerequisites' },
    { label: 'Get API Key', targetId: 'get-api-key' },
    { label: 'Key handling', targetId: 'key-handling' },
  ];

  readonly documentSections: PageSectionLink[] = [
    { label: 'Overview', targetId: 'documents-overview' },
    { label: 'Create Document', targetId: 'create-document' },
    { label: 'Update Document', targetId: 'update-document' },
    { label: 'Document Details and Status', targetId: 'document-details-status' },
    { label: 'Send Reminders', targetId: 'send-reminders' },
    { label: 'Revoke Document', targetId: 'revoke-document' },
    { label: 'Send Document from Template', targetId: 'send-document-from-template' },
    { label: 'Download Document', targetId: 'download-document' },
    { label: 'Delete Document', targetId: 'delete-document' },
    { label: 'Required fields', targetId: 'required-fields' },
    { label: 'Request and response', targetId: 'request-response' },
    { label: 'Embedded Configure Fields', targetId: 'embedded-configure-fields' },
    { label: 'Refresh Configure URL', targetId: 'refresh-configure-url' },
  ];

  readonly templateSections: PageSectionLink[] = [
    { label: 'Overview', targetId: 'templates-overview' },
    { label: 'Create Template', targetId: 'create-template' },
    { label: 'Update Template', targetId: 'update-template' },
    { label: 'Template Details', targetId: 'template-details' },
    { label: 'Delete Template', targetId: 'delete-template' },
    { label: 'Embedded Configure Fields', targetId: 'embedded-template-configure-fields' },
    { label: 'Refresh Configure URL', targetId: 'refresh-template-configure-url' },
  ];

  readonly senderIdentitySections: PageSectionLink[] = [
    { label: 'Overview', targetId: 'sender-identities-overview' },
    { label: 'Create Sender Identity', targetId: 'create-sender-identity' },
    { label: 'Update Sender Identity', targetId: 'update-sender-identity' },
    { label: 'Resend Invitation', targetId: 'resend-sender-identity-invitation' },
    { label: 'Rerequest Approval', targetId: 'rerequest-sender-identity-approval' },
    { label: 'Delete Sender Identity', targetId: 'delete-sender-identity' },
  ];

  readonly brandSections: PageSectionLink[] = [
    { label: 'Overview', targetId: 'brands-overview' },
    { label: 'List Brands', targetId: 'list-brands' },
    { label: 'Create Brand', targetId: 'create-brand' },
    { label: 'Update Brand', targetId: 'update-brand' },
    { label: 'Delete Brand', targetId: 'delete-brand' },
  ];

  readonly webhookSections: PageSectionLink[] = [
    { label: 'Overview', targetId: 'webhooks-overview' },
    { label: 'Document Events', targetId: 'document-events' },
    { label: 'Sender Identity Events', targetId: 'sender-identity-events' },
    { label: 'Template Events', targetId: 'template-events' },
  ];

  readonly apiKeyOverviewCards: GuideListItem[] = [
    {
      title: 'Purpose',
      detail: 'An API key is required for all public document endpoints.',
    },
    {
      title: 'Key types',
      detail: 'Use Sandbox while building and Live for production traffic.',
    },
    {
      title: 'Visibility',
      detail: 'The full key value is shown once at creation time.',
    },
  ];

  readonly documentOverviewCards: GuideListItem[] = [
    {
      title: 'Create draft',
      detail: 'Upload a PDF and create a draft document before field placement.',
    },
    {
      title: 'Send from template',
      detail: 'Use a stored template with configured fields to create and send a real document in one API call.',
    },
    {
      title: 'Configure fields',
      detail: 'Use `configureFieldsUrl` to open the embedded public field configuration flow.',
    },
    {
      title: 'Check status later',
      detail: 'Fetch document details, recipient progress, timeline, and final signed file metadata through a sanitized read-only response.',
    },
    {
      title: 'Reuse later',
      detail: 'Refresh the configure URL later with the API key and `documentId`.',
    },
    {
      title: 'Download final PDF',
      detail: 'Download the final signed PDF after the document is completed and final PDF generation is done.',
    },
    {
      title: 'Move to trash',
      detail: 'Soft-delete eligible documents and remove them from active document lookups without permanently deleting stored files.',
    },
  ];

  readonly templateOverviewCards: GuideListItem[] = [
    {
      title: 'Create template shell',
      detail: 'Upload a PDF and create a reusable template with roles, settings, and optional fields.',
    },
    {
      title: 'Configure fields',
      detail: 'Use `configureFieldsUrl` to open the embedded public template editor in an iframe.',
    },
    {
      title: 'Refresh later',
      detail: 'Request a fresh template configure URL when you need to reopen the embedded editor.',
    },
  ];

  readonly senderIdentityOverviewCards: GuideListItem[] = [
    {
      title: 'Request approval',
      detail: 'Create a sender identity when you need another email address to approve send-on-behalf-of access.',
    },
    {
      title: 'State flow',
      detail: 'Sender identities move through `pending`, `denied`, and `verified` depending on the approval decision.',
    },
    {
      title: 'Retry paths',
      detail: 'Use `Resend Invitation` for `pending` identities and `Rerequest Approval` for identities that were denied.',
    },
    {
      title: 'Brand optional',
      detail: 'Attach an owned `brandId` when approval emails and pages should use a specific brand.',
    },
  ];

  readonly brandOverviewCards: GuideListItem[] = [
    {
      title: 'List existing brands',
      detail: 'Fetch every brand available to the API-key owner in one pageless response.',
    },
    {
      title: 'Create branded senders',
      detail: 'Create brands that can later be assigned to documents, templates, and sender identities.',
    },
    {
      title: 'Brand identifier',
      detail: 'Each successful create returns a `brandId` that you can store and reuse in later public API calls.',
    },
    {
      title: 'Default behavior',
      detail: 'Your first brand becomes default automatically, and creating a new default brand clears the previous default.',
    },
  ];

  readonly webhookOverviewCards: GuideListItem[] = [
    {
      title: 'Delivery model',
      detail:
        'Webhook deliveries are queued asynchronously for subscribed events and sent with a shared outer envelope.',
    },
    {
      title: 'Retry behavior',
      detail:
        'Failed webhook deliveries retry automatically up to 15 total attempts, using exponential backoff that starts at 30 seconds.',
    },
    {
      title: 'Source of truth',
      detail: 'This reference follows actual backend publish calls and payload shapes, not stale allowlist-only entries.',
    },
    {
      title: 'Event data',
      detail: 'Each event block documents the `data` payload because that is the part integrations typically consume most.',
    },
    {
      title: 'Subject mapping',
      detail: 'The webhook `subject` always points back to the document, sender identity, or template that raised the event.',
    },
  ];

  readonly webhookEnvelopeFields: GuideFieldItem[] = [
    {
      field: 'event',
      status: 'Required',
      detail: 'The emitted webhook event key, such as `document.sent` or `template.created`.',
    },
    {
      field: 'occurredAt',
      status: 'Required',
      detail: 'ISO timestamp for when the event was published by the backend.',
    },
    {
      field: 'subject',
      status: 'Required',
      detail: 'Object describing the primary webhook subject with `type` and `id`.',
    },
    {
      field: 'data',
      status: 'Required',
      detail: 'Event-specific payload fields for the emitted webhook.',
    },
  ];

  readonly webhookEnvelopeExample = `{
  "event": "document.sent",
  "occurredAt": "2026-07-13T11:30:00.000Z",
  "subject": {
    "type": "document",
    "id": "6871f27d1f95d3e3a3f7a123"
  },
  "data": {
    "id": "6871f27d1f95d3e3a3f7a123",
    "title": "Offer Letter",
    "status": "waiting_for_others",
    "recipientIds": ["recipient_1"],
    "recipientCount": 1
  }
}`;

  readonly documentWebhookEvents: WebhookEventDoc[] = [
    {
      event: 'document.sent',
      meaning: 'Published after invite recipients have been queued through the send pipeline.',
      subject: 'Subject type is `document` and subject id is the document id.',
      notes: 'This event is emitted from the worker after send processing begins, not directly from the create or send route.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "waiting_for_others",
  "recipientIds": ["recipient_1"],
  "recipientCount": 1
}`,
    },
    {
      event: 'document.signed',
      meaning: 'Published when an individual recipient submits their signature.',
      subject: 'Subject type is `document` and subject id is the document id.',
      notes: '`completedAllRecipients` becomes `true` for the final signer, but final PDF readiness is signaled later by `document.completed`.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "waiting_for_others",
  "recipientId": "recipient_1",
  "recipientName": "Jane Doe",
  "recipientEmail": "jane@example.com",
  "completedAllRecipients": false
}`,
    },
    {
      event: 'document.completed',
      meaning: 'Published only after all recipients have completed signing and final PDF generation is done.',
      subject: 'Subject type is `document` and subject id is the completed document id.',
      notes: 'This event is emitted from async finalization after the final signed PDF is ready.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "completed",
  "recipientCount": 1,
  "tags": ["offer"]
}`,
    },
    {
      event: 'document.declined',
      meaning: 'Published when a recipient explicitly declines the signature request.',
      subject: 'Subject type is `document` and subject id is the declined document id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "declined",
  "recipientId": "recipient_1",
  "recipientName": "Jane Doe",
  "recipientEmail": "jane@example.com",
  "reason": "Need legal review first"
}`,
    },
    {
      event: 'document.revoked',
      meaning: 'Published when the sender revokes the signature request.',
      subject: 'Subject type is `document` and subject id is the revoked document id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "revoked",
  "reason": "Sent with the wrong details"
}`,
    },
    {
      event: 'document.expired',
      meaning: 'Published when the request reaches its expiry time without completion.',
      subject: 'Subject type is `document` and subject id is the expired document id.',
      notes: 'This event is emitted by the worker when expiry is resolved.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "expired",
  "recipientCount": 1
}`,
    },
    {
      event: 'document.viewed',
      meaning: 'Published on the first active view transition when a recipient opens the signing request.',
      subject: 'Subject type is `document` and subject id is the viewed document id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "waiting_for_others",
  "recipientId": "recipient_1",
  "recipientName": "Jane Doe",
  "recipientEmail": "jane@example.com"
}`,
    },
    {
      event: 'document.delivery_failed',
      meaning: 'Published after email delivery retries are exhausted for an affected recipient.',
      subject: 'Subject type is `document` and subject id is the document id moved into `needs_attention`.',
      notes: 'This durable failure path marks the recipient as `delivery_failed` and the document status as `needs_attention`.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "needs_attention",
  "recipientId": "recipient_1",
  "recipientName": "Jane Doe",
  "recipientEmail": "jane@example.com",
  "error": "Mailbox unavailable"
}`,
    },
    {
      event: 'document.send_failed',
      meaning: 'Published when the send pipeline fails after the document has already entered worker processing.',
      subject: 'Subject type is `document` and subject id is the failed document id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "needs_attention",
  "error": "Unable to dispatch document"
}`,
    },
    {
      event: 'document.draft_created',
      meaning: 'Published when a draft document is created successfully in authenticated or public draft-create flows.',
      subject: 'Subject type is `document` and subject id is the created draft document id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "draft",
  "recipientCount": 1,
  "sourceType": "upload"
}`,
    },
    {
      event: 'document.reminder',
      meaning: 'Published when the sender manually queues reminder emails for eligible recipients.',
      subject: 'Subject type is `document` and subject id is the reminded document id.',
      notes: 'This event means reminder jobs were queued. It does not guarantee downstream delivery success.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "waiting_for_others",
  "recipientIds": ["recipient_1"]
}`,
    },
    {
      event: 'document.edited',
      meaning: 'Published after a document draft is updated successfully.',
      subject: 'Subject type is `document` and subject id is the edited document id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "title": "Offer Letter",
  "status": "draft",
  "recipientCount": 1,
  "sourceType": "upload"
}`,
    },
  ];

  readonly senderIdentityWebhookEvents: WebhookEventDoc[] = [
    {
      event: 'sender_identity.created',
      meaning: 'Published when a sender identity is created successfully in `pending` state.',
      subject: 'Subject type is `sender_identity` and subject id is the sender identity id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Approver User",
  "email": "approver@example.com",
  "status": "pending",
  "brandName": "Acme Brand"
}`,
    },
    {
      event: 'sender_identity.updated',
      meaning: 'Published after sender identity metadata is updated or a denied identity is rerequested back to `pending`.',
      subject: 'Subject type is `sender_identity` and subject id is the sender identity id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Updated Approver User",
  "email": "approver@example.com",
  "status": "pending",
  "brandName": "Acme Brand"
}`,
    },
    {
      event: 'sender_identity.deleted',
      meaning: 'Published after the owned sender identity is deleted successfully.',
      subject: 'Subject type is `sender_identity` and subject id is the deleted sender identity id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Approver User",
  "email": "approver@example.com",
  "status": "pending",
  "brandName": "Acme Brand"
}`,
    },
    {
      event: 'sender_identity.revoked',
      meaning: 'Published when the owner revokes permission to send on behalf of that identity.',
      subject: 'Subject type is `sender_identity` and subject id is the revoked sender identity id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Approver User",
  "email": "approver@example.com",
  "status": "revoked",
  "approvedAt": null,
  "deniedAt": null,
  "revokedAt": "2026-07-13T11:30:00.000Z",
  "brandName": "Acme Brand"
}`,
    },
    {
      event: 'sender_identity.verified',
      meaning: 'Published when the invite recipient approves the sender identity.',
      subject: 'Subject type is `sender_identity` and subject id is the verified sender identity id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Approver User",
  "email": "approver@example.com",
  "status": "verified",
  "approvedAt": "2026-07-13T11:30:00.000Z",
  "deniedAt": null,
  "revokedAt": null,
  "brandName": "Acme Brand"
}`,
    },
    {
      event: 'sender_identity.denied',
      meaning: 'Published when the invite recipient explicitly rejects the sender identity invitation.',
      subject: 'Subject type is `sender_identity` and subject id is the denied sender identity id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Approver User",
  "email": "approver@example.com",
  "status": "denied",
  "approvedAt": null,
  "deniedAt": "2026-07-13T11:30:00.000Z",
  "revokedAt": null,
  "brandName": "Acme Brand"
}`,
    },
  ];

  readonly templateWebhookEvents: WebhookEventDoc[] = [
    {
      event: 'template.created',
      meaning: 'Published once when a draft template first transitions to created after successful field configuration.',
      subject: 'Subject type is `template` and subject id is the template id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Offer Letter Template",
  "description": "US hiring packet",
  "status": "created",
  "tags": ["offer-letter"]
}`,
    },
    {
      event: 'template.edited',
      meaning: 'Published after a template update is persisted successfully.',
      subject: 'Subject type is `template` and subject id is the edited template id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Offer Letter Template",
  "description": "Updated US hiring packet",
  "status": "active"
}`,
    },
    {
      event: 'template.deleted',
      meaning: 'Published after a template is deleted successfully.',
      subject: 'Subject type is `template` and subject id is the deleted template id.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Offer Letter Template",
  "description": "US hiring packet",
  "status": "deleted"
}`,
    },
    {
      event: 'template.send_failed',
      meaning: 'Published when a template-origin document enters the send pipeline but ultimately fails.',
      subject: 'Subject type is `template` and subject id is the source template id.',
      notes: 'This complements `document.send_failed` for documents created from templates.',
      payload: `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Offer Letter Template",
  "status": "send_failed",
  "documentId": "6892f3ac2d7b6d5f4a2e1c00",
  "documentTitle": "Offer Letter - Batch 14",
  "error": "Unable to dispatch document"
}`,
    },
  ];

  readonly brandListChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `GET /api/v1/public/brands`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Response shape',
      detail: 'A successful response returns pageless JSON with `total` and `items`.',
    },
    {
      title: 'Request body',
      detail: 'No request body is required for this endpoint.',
    },
    {
      title: 'Sorting',
      detail: 'Default brands appear first. Remaining brands are ordered by newest-first.',
    },
    {
      title: 'Reuse later',
      detail: 'Store returned `brandId` values for later document, template, and sender-identity requests.',
    },
  ];

  readonly brandListResponseFields: GuideFieldItem[] = [
    {
      field: 'total',
      status: 'Required',
      detail: 'Total number of brands owned by the API-key account.',
    },
    {
      field: 'items[]',
      status: 'Required',
      detail: 'Array of full brand summary objects sorted with default brands first.',
    },
    {
      field: 'items[].id',
      status: 'Required',
      detail: 'Internal brand record id.',
    },
    {
      field: 'items[].brandId',
      status: 'Required',
      detail: 'Reusable public brand identifier for later API calls.',
    },
    {
      field: 'items[].name',
      status: 'Required',
      detail: 'Brand display name.',
    },
    {
      field: 'items[].isDefault',
      status: 'Required',
      detail: 'Indicates whether the brand is the owner account default.',
    },
    {
      field: 'items[].logoDataUrl',
      status: 'Optional',
      detail: 'Brand logo data URL when a logo has been saved.',
    },
    {
      field: 'items[].emailDisplayName',
      status: 'Optional',
      detail: 'Sender display name used in brand-driven email experiences.',
    },
    {
      field: 'items[].emailBackgroundColor',
      status: 'Required',
      detail: 'Brand email background color.',
    },
    {
      field: 'items[].primaryButtonColor',
      status: 'Required',
      detail: 'Brand primary button color.',
    },
    {
      field: 'items[].primaryButtonTextColor',
      status: 'Required',
      detail: 'Brand primary button text color.',
    },
    {
      field: 'items[].showPlatformBranding',
      status: 'Required',
      detail: 'Controls whether platform branding stays visible in brand-driven output.',
    },
    {
      field: 'items[].createdAt',
      status: 'Required',
      detail: 'Brand creation timestamp.',
    },
    {
      field: 'items[].updatedAt',
      status: 'Required',
      detail: 'Brand last update timestamp.',
    },
  ];

  readonly brandListErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
  ];

  readonly brandCreateChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `POST /api/v1/public/brands`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Request body',
      detail: 'Send JSON using the current brand create fields supported by the product.',
    },
    {
      title: 'Response',
      detail: 'A successful create returns both the internal `id` and the reusable `brandId`.',
    },
    {
      title: 'Default handling',
      detail: 'The first brand becomes default automatically, and a new default brand clears the prior default brand.',
    },
  ];

  readonly brandCreateFields: GuideFieldItem[] = [
    {
      field: 'name',
      status: 'Required',
      detail: 'Brand display name. Duplicate names are rejected for the same owner ignoring case and extra spacing.',
    },
    {
      field: 'isDefault',
      status: 'Optional',
      detail: 'Marks the brand as default. The first brand becomes default even when this value is false.',
    },
    {
      field: 'logoDataUrl',
      status: 'Optional',
      detail: 'Base64 data URL for a PNG, JPG, JPEG, or SVG logo image.',
    },
    {
      field: 'emailDisplayName',
      status: 'Optional',
      detail: 'Optional sender display name shown in brand-driven email experiences.',
    },
    {
      field: 'emailBackgroundColor',
      status: 'Required',
      detail: 'Brand email background color in 6-digit hex format.',
    },
    {
      field: 'primaryButtonColor',
      status: 'Required',
      detail: 'Primary action button color in 6-digit hex format.',
    },
    {
      field: 'primaryButtonTextColor',
      status: 'Required',
      detail: 'Primary action button text color in 6-digit hex format.',
    },
    {
      field: 'showPlatformBranding',
      status: 'Required',
      detail: 'Controls whether platform branding remains visible in brand-driven output.',
    },
  ];

  readonly brandCreateErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned for invalid color values, invalid `logoDataUrl`, or other schema violations.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '409 Brand name already exists',
      detail: 'Returned when the API-key owner already has a brand with the same normalized name.',
    },
  ];

  readonly brandUpdateChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `PATCH /api/v1/public/brands/{brandId}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Update style',
      detail: 'Send only the brand fields you want to change. The route applies a partial update, not a full replace.',
    },
    {
      title: 'Default handling',
      detail: 'Setting `isDefault` to true clears the previous default brand. A single remaining brand stays default.',
    },
    {
      title: 'Scope',
      detail: 'This route updates brand data only. Brand email templates continue to use their existing email-template endpoints.',
    },
  ];

  readonly brandUpdateFields: GuideFieldItem[] = [
    {
      field: 'brandId',
      status: 'Required',
      detail: 'Path parameter. Owned brand identifier to update.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
    {
      field: 'name',
      status: 'Optional',
      detail: 'Updated brand display name. Duplicate normalized names are rejected for the same owner.',
    },
    {
      field: 'isDefault',
      status: 'Optional',
      detail: 'Set to `true` to make the brand default. The API does not allow your only brand to lose default status.',
    },
    {
      field: 'logoDataUrl',
      status: 'Optional',
      detail: 'Updated base64 data URL for a PNG, JPG, JPEG, or SVG logo image.',
    },
    {
      field: 'emailDisplayName',
      status: 'Optional',
      detail: 'Updated sender display name used in brand-driven email experiences.',
    },
    {
      field: 'emailBackgroundColor',
      status: 'Optional',
      detail: 'Updated brand email background color in 6-digit hex format.',
    },
    {
      field: 'primaryButtonColor',
      status: 'Optional',
      detail: 'Updated primary action button color in 6-digit hex format.',
    },
    {
      field: 'primaryButtonTextColor',
      status: 'Optional',
      detail: 'Updated primary action button text color in 6-digit hex format.',
    },
    {
      field: 'showPlatformBranding',
      status: 'Optional',
      detail: 'Controls whether platform branding remains visible in brand-driven output.',
    },
  ];

  readonly brandUpdateErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned for invalid color values, invalid `logoDataUrl`, empty update payloads, or other schema violations.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Brand not found',
      detail: 'Returned when `brandId` is missing, deleted, or not owned by the API-key owner.',
    },
    {
      title: '409 Brand name already exists',
      detail: 'Returned when the API-key owner already has another brand with the same normalized name.',
    },
  ];

  readonly brandDeleteChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `DELETE /api/v1/public/brands/{brandId}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Request body',
      detail: 'No request body is required for this endpoint.',
    },
    {
      title: 'Delete type',
      detail: 'This route permanently deletes the brand and removes its saved brand email-template overrides.',
    },
    {
      title: 'Default restriction',
      detail: 'The current default brand cannot be deleted. Set another brand as default first.',
    },
  ];

  readonly brandDeleteFields: GuideFieldItem[] = [
    {
      field: 'brandId',
      status: 'Required',
      detail: 'Path parameter. Owned brand identifier to delete.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
    {
      field: 'Response',
      status: 'Required',
      detail: 'Returns `204 No Content` when the brand is deleted successfully.',
    },
  ];

  readonly brandDeleteErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Brand not found',
      detail: 'Returned when `brandId` is missing, already deleted, or not owned by the API-key owner.',
    },
    {
      title: '409 Default brand cannot be deleted',
      detail: 'Returned when the target brand is currently the default brand for the account.',
    },
  ];

  readonly senderIdentityCreateChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `POST /api/v1/public/sender-identities`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Request body',
      detail: 'Send JSON with the sender identity `name`, `email`, and optional `brandId`.',
    },
    {
      title: 'Approval flow',
      detail: 'The identity is created in `pending` status and the approval flow continues through the existing approval route.',
    },
    {
      title: 'Side effects',
      detail: 'The system sends the approval email automatically and publishes `sender_identity.created`.',
    },
  ];

  readonly senderIdentityCreateFields: GuideFieldItem[] = [
    {
      field: 'name',
      status: 'Required',
      detail: 'Sender identity display name.',
    },
    {
      field: 'email',
      status: 'Required',
      detail: 'Sender identity email address. Must be valid and unique for the same API-key owner.',
    },
    {
      field: 'brandId',
      status: 'Optional',
      detail: 'Owned brand identifier to use for the approval email and approval page branding.',
    },
  ];

  readonly senderIdentityResponseFields: GuideFieldItem[] = [
    {
      field: 'id',
      status: 'Required',
      detail: 'Created sender identity id.',
    },
    {
      field: 'name',
      status: 'Required',
      detail: 'Sender identity display name.',
    },
    {
      field: 'email',
      status: 'Required',
      detail: 'Sender identity email address.',
    },
    {
      field: 'status',
      status: 'Required',
      detail: 'Initial sender identity status. New records are returned as `pending`.',
    },
    {
      field: 'createdAt',
      status: 'Required',
      detail: 'Timestamp for sender identity creation.',
    },
    {
      field: 'approvedAt',
      status: 'Required',
      detail: 'Approval timestamp. This is `null` until the identity is approved.',
    },
    {
      field: 'brand',
      status: 'Required',
      detail: 'Resolved brand display name, or `-` when no brand is attached.',
    },
    {
      field: 'language',
      status: 'Required',
      detail: 'Current response language label returned by the product.',
    },
  ];

  readonly senderIdentityErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned for invalid `name` or `email` values.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Brand not found',
      detail: 'Returned when `brandId` is invalid or does not belong to the API-key owner.',
    },
    {
      title: '409 Sender identity already exists',
      detail: 'Returned when the same owner already has a sender identity for the provided email address.',
    },
  ];

  readonly senderIdentityUpdateChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `PATCH /api/v1/public/sender-identities/{id}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Supported fields',
      detail: 'Send JSON with required `name` and optional `brandId`.',
    },
    {
      title: 'Brand clearing',
      detail: 'Send an empty `brandId` to remove the current brand association.',
    },
    {
      title: 'Webhook',
      detail: 'Successful updates publish `sender_identity.updated` for the owning account.',
    },
  ];

  readonly senderIdentityUpdateFields: GuideFieldItem[] = [
    {
      field: 'id',
      status: 'Required',
      detail: 'Path parameter. Owned sender identity id to update.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
    {
      field: 'name',
      status: 'Required',
      detail: 'Updated sender identity display name.',
    },
    {
      field: 'brandId',
      status: 'Optional',
      detail: 'Owned brand identifier. Leave blank to clear the existing brand.',
    },
    {
      field: 'email',
      status: 'Optional',
      detail: 'Not supported in v1. Email changes are rejected by the public update schema.',
    },
  ];

  readonly senderIdentityUpdateErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned for invalid `name`, unsupported `email`, or other schema violations.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Sender identity not found',
      detail: 'Returned when the sender identity does not exist or is not owned by the API-key owner.',
    },
    {
      title: '404 Brand not found',
      detail: 'Returned when `brandId` is invalid or does not belong to the API-key owner.',
    },
  ];

  readonly senderIdentityDeleteChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `DELETE /api/v1/public/sender-identities/{id}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Request body',
      detail: 'No request body is required.',
    },
    {
      title: 'Response',
      detail: 'A successful delete returns `204 No Content`.',
    },
    {
      title: 'Webhook',
      detail: 'Successful deletes publish `sender_identity.deleted` for the owning account.',
    },
  ];

  readonly senderIdentityDeleteFields: GuideFieldItem[] = [
    {
      field: 'id',
      status: 'Required',
      detail: 'Path parameter. Owned sender identity id to delete.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly senderIdentityDeleteErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Sender identity not found',
      detail: 'Returned when the sender identity does not exist or is not owned by the API-key owner.',
    },
  ];

  readonly senderIdentityResendChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `POST /api/v1/public/sender-identities/resend-verification`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Lookup',
      detail: 'Provide the target sender identity through the required `email` query parameter.',
    },
    {
      title: 'Pending only',
      detail: 'Only sender identities still in `pending` status can receive another invitation.',
    },
    {
      title: 'Response',
      detail: 'A successful resend returns `204 No Content`.',
    },
  ];

  readonly senderIdentityResendFields: GuideFieldItem[] = [
    {
      field: 'email',
      status: 'Required',
      detail: 'Query parameter. Sender identity email used to find the owned pending sender identity.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly senderIdentityResendErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned when the `email` query parameter is missing or invalid.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Sender identity not found',
      detail: 'Returned when the sender identity does not exist or is not owned by the API-key owner.',
    },
    {
      title: '409 Sender identity is already verified',
      detail: 'Returned when the sender identity is no longer in `pending` status.',
    },
  ];

  readonly senderIdentityRerequestChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `POST /api/v1/public/sender-identities/rerequest`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Lookup',
      detail: 'Provide the target sender identity through the required `email` query parameter.',
    },
    {
      title: 'Denied only',
      detail: 'Only sender identities in `denied` status are eligible for rerequest.',
    },
    {
      title: 'Behavior',
      detail: 'Rerequest resets the sender identity to `pending` and sends a fresh approval email.',
    },
  ];

  readonly senderIdentityRerequestFields: GuideFieldItem[] = [
    {
      field: 'email',
      status: 'Required',
      detail: 'Query parameter. Sender identity email used to find the owned denied sender identity.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly senderIdentityRerequestErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned when the `email` query parameter is missing or invalid.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Sender identity not found',
      detail: 'Returned when the sender identity does not exist or is not owned by the API-key owner.',
    },
    {
      title: '409 Sender identity is not denied',
      detail: 'Returned when the sender identity is still `pending` or already `verified`.',
    },
  ];

  readonly apiMeta: GuideMetaItem[] = [
    {
      label: 'Method',
      value: 'POST',
    },
    {
      label: 'Endpoint',
      value: '/api/v1/public/documents',
    },
    {
      label: 'Auth',
      value: 'Bearer API key',
    },
    {
      label: 'Content type',
      value: 'multipart/form-data',
    },
    {
      label: 'Returns',
      value: 'documentId, status, configureFieldsUrl',
    },
  ];

  readonly templateApiMeta: GuideMetaItem[] = [
    {
      label: 'Method',
      value: 'POST',
    },
    {
      label: 'Endpoint',
      value: '/api/v1/public/templates',
    },
    {
      label: 'Auth',
      value: 'Bearer API key',
    },
    {
      label: 'Content type',
      value: 'multipart/form-data',
    },
    {
      label: 'Returns',
      value: 'templateId, configureFieldsUrl',
    },
  ];

  readonly steps: ApiKeyGuideStep[] = [
    {
      title: 'Sign in',
      detail: 'Sign in to the dashboard, or create an account first if you do not have one yet.',
    },
    {
      title: 'Open API > API Key',
      detail: 'In the dashboard sidebar, open API and select API Key.',
    },
    {
      title: 'Click Generate API Key',
      detail: 'Create a new key from the API key management page.',
    },
    {
      title: 'Choose Sandbox or Live',
      detail: 'Use Sandbox for testing and Live for production traffic.',
    },
    {
      title: 'Enter an API key name',
      detail: 'Use a clear label so the key is easy to identify later.',
    },
    {
      title: 'Optionally set expiry',
      detail: 'Set an expiry date only when you want the key to stop working automatically.',
    },
    {
      title: 'Generate and copy the key',
      detail: 'Copy the key immediately and store it in your secret manager or vault.',
    },
  ];

  readonly notes: ApiKeyGuideNote[] = [
    {
      title: 'Shown once only',
      detail: 'The full API key is shown only once after creation.',
    },
    {
      title: 'Store it securely',
      detail: 'Treat the key like a password and store it in a secure credential store.',
    },
    {
      title: 'Sandbox is for testing',
      detail: 'Use sandbox keys while building and validating the integration.',
    },
    {
      title: 'Sandbox document behavior',
      detail: 'Sandbox documents are deleted after 14 days and include a TEST watermark.',
    },
  ];

  readonly createDocumentChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `POST /api/v1/public/documents`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'File upload',
      detail: 'Attach the source PDF in the `file` multipart field.',
    },
    {
      title: 'Payload field',
      detail: 'Send recipients, document, settings, and optional data in the `payload` JSON string.',
    },
    {
      title: 'Response',
      detail: 'The API returns a draft `documentId` and a `configureFieldsUrl` for the next step.',
    },
  ];

  readonly embedGuideItems: GuideListItem[] = [
    {
      title: 'Iframe URL',
      detail: 'Use the returned `configureFieldsUrl` as the iframe source for the embedded field configuration UI.',
    },
    {
      title: 'Editable state',
      detail: 'Field editing is allowed only while the document status is `draft`.',
    },
    {
      title: 'Read-only after send',
      detail: 'After send and later statuses, the same configure URL remains accessible in read-only mode.',
    },
    {
      title: 'Save event',
      detail: 'A successful save posts `document-config.saved` to the parent window for iframe integrations.',
    },
  ];

  readonly updateDocumentChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `PATCH /api/v1/public/documents/{documentId}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Draft only',
      detail: 'Only documents in `draft` status can be updated through this public endpoint.',
    },
    {
      title: 'Request body',
      detail: 'Send a partial JSON body with only the step-1 document fields you want to change.',
    },
    {
      title: 'Unsupported fields',
      detail: 'File replacement, `sourceFile`, `fields`, and send/status mutations are not supported in v1.',
    },
  ];

  readonly updateDocumentParams: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted draft document id to update.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly updateDocumentFields: GuideFieldItem[] = [
    {
      field: 'recipients',
      status: 'Optional',
      detail: 'Step-1 recipients list. Recipient ids, order values, and role ids must remain unique after normalization.',
    },
    {
      field: 'ccEmails',
      status: 'Optional',
      detail: 'CC email recipients.',
    },
    {
      field: 'document',
      status: 'Optional',
      detail: 'Document title/message object. Title must remain valid when updated.',
    },
    {
      field: 'tags',
      status: 'Optional',
      detail: 'Document tags.',
    },
    {
      field: 'documentSettings',
      status: 'Optional',
      detail: 'Document settings. `brandId` must belong to the API-key owner if provided.',
    },
    {
      field: 'signingOrderEnabled',
      status: 'Optional',
      detail: 'Toggle signing-order behavior.',
    },
    {
      field: 'file',
      status: 'Optional',
      detail: 'Not supported in v1. Source PDF replacement is not allowed.',
    },
    {
      field: 'sourceFile',
      status: 'Optional',
      detail: 'Not supported in v1. Source file replacement requests are rejected.',
    },
    {
      field: 'fields',
      status: 'Optional',
      detail: 'Not supported in v1. Field placement remains in the configure-fields flow.',
    },
  ];

  readonly updateDocumentResponseFields: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Resolved owned document id returned by the API.',
    },
    {
      field: 'status',
      status: 'Required',
      detail: 'Document status. Remains `draft` for editable public updates.',
    },
    {
      field: 'expiresAt',
      status: 'Required',
      detail: 'Current document expiry timestamp.',
    },
    {
      field: 'sourceFile',
      status: 'Required',
      detail: 'Stored source PDF metadata. The uploaded file remains unchanged in v1.',
    },
    {
      field: 'updatedAt',
      status: 'Required',
      detail: 'Timestamp of the latest document update.',
    },
    {
      field: 'configureFieldsUrl',
      status: 'Required',
      detail: 'Public configure URL for continuing field placement after the update.',
    },
  ];

  readonly updateDocumentErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned for invalid recipients, invalid document payload, unsupported keys such as `sourceFile` or `fields`, or unowned `brandId`.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Document not found',
      detail: 'Returned when the document does not exist, is deleted, or is not owned by the API key owner.',
    },
    {
      title: '409 Only draft documents can be updated via the public API',
      detail: 'Returned when the document has already left the `draft` state.',
    },
  ];

  readonly documentDetailsChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `GET /api/v1/public/documents/{documentId}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Path parameter',
      detail: '`documentId` is required and must belong to the authenticated API-key owner.',
    },
    {
      title: 'Response shape',
      detail: 'A successful response returns document details, recipient status, file metadata, timeline, submissions, and sender-identity context when present.',
    },
    {
      title: 'Sanitized output',
      detail: 'Internal signing/authentication and delivery fields are excluded from the public response.',
    },
  ];

  readonly documentDetailsParams: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted document id to inspect.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly documentDetailsResponseFields: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Resolved owned document id returned by the API.',
    },
    {
      field: 'status',
      status: 'Required',
      detail: 'Current document status such as `draft`, `waiting_for_others`, `completed`, `declined`, `revoked`, or `expired`.',
    },
    {
      field: 'recipients',
      status: 'Required',
      detail: 'Public-safe recipient lifecycle details including role, status, timestamps, and reassignment metadata.',
    },
    {
      field: 'timeline',
      status: 'Required',
      detail: 'Document timeline entries recorded for sender, recipient, and system events.',
    },
    {
      field: 'sourceFile',
      status: 'Required',
      detail: 'Stored source file metadata for the original upload or template-derived source PDF.',
    },
    {
      field: 'finalSignedFile',
      status: 'Optional',
      detail: 'Final signed PDF metadata when the document has already been completed and finalized.',
    },
    {
      field: 'submissions',
      status: 'Required',
      detail: 'Recipient submission payloads captured for completed signing actions.',
    },
    {
      field: 'senderIdentity',
      status: 'Optional',
      detail: 'Verified sender-identity snapshot used for send-on-behalf-of documents when present.',
    },
  ];

  readonly documentDetailsErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Document not found',
      detail: 'Returned when the document does not exist, is deleted, or is not owned by the API key owner.',
    },
  ];

  readonly sendRemindersChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `POST /api/v1/public/documents/{documentId}/remind`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Document scope',
      detail: 'This route targets one owned document at a time and can queue reminders for multiple eligible recipients on that document.',
    },
    {
      title: 'Eligibility',
      detail: 'The API automatically selects recipients currently eligible for reminders based on document status and recipient lifecycle state.',
    },
    {
      title: 'Signing order',
      detail: 'When signing order is enabled, reminders are queued only for the next actionable recipient(s).',
    },
    {
      title: 'Delivery model',
      detail: 'A successful response confirms reminder jobs were queued. It does not guarantee downstream email delivery success.',
    },
  ];

  readonly sendRemindersParams: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted document id whose eligible recipients should be reminded.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly sendRemindersResponseFields: GuideFieldItem[] = [
    {
      field: 'queuedRecipientIds',
      status: 'Required',
      detail: 'Recipient ids for which reminder jobs were queued in this request.',
    },
    {
      field: 'document',
      status: 'Required',
      detail: 'Updated public-safe document snapshot after the reminder queue event is appended to the timeline.',
    },
    {
      field: 'document.timeline',
      status: 'Required',
      detail: 'Includes the newly appended `document.reminders_queued` timeline event.',
    },
  ];

  readonly sendRemindersErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Document not found',
      detail: 'Returned when the document does not exist, is deleted, or is not owned by the API key owner.',
    },
    {
      title: '409 Cannot send reminder',
      detail: 'Returned when the document is not in `waiting_for_others` status.',
    },
    {
      title: '409 No recipients are currently eligible for a reminder',
      detail: 'Returned when none of the document recipients are currently in a remindable state.',
    },
  ];

  readonly revokeDocumentChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `POST /api/v1/public/documents/{documentId}/revoke`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Document effect',
      detail: 'Revoking a document stops the signing process. Recipients can no longer view or sign it.',
    },
    {
      title: 'Eligibility',
      detail: 'Only non-terminal documents can be revoked. Terminal statuses such as `completed`, `declined`, `revoked`, and `expired` are rejected.',
    },
    {
      title: 'Optional reason',
      detail: 'You may send an optional `reason` string. When present, it is stored as the revoked document status reason.',
    },
    {
      title: 'Notifications',
      detail: 'The route queues revoke notifications and publishes `document.revoked`, but the response itself confirms the revoke action rather than downstream email delivery.',
    },
  ];

  readonly revokeDocumentParams: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted document id to revoke.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
    {
      field: 'reason',
      status: 'Optional',
      detail: 'Optional request-body field explaining why the document was revoked. Maximum length is 1000 characters.',
    },
  ];

  readonly revokeDocumentResponseFields: GuideFieldItem[] = [
    {
      field: 'status',
      status: 'Required',
      detail: 'Returned as `revoked` after the revoke action succeeds.',
    },
    {
      field: 'statusReason',
      status: 'Optional',
      detail: 'Trimmed revoke reason when one was provided in the request.',
    },
    {
      field: 'revokedAt',
      status: 'Required',
      detail: 'Timestamp recorded when the document was revoked.',
    },
    {
      field: 'recipients',
      status: 'Required',
      detail: 'Public-safe recipient list after revocation. Non-terminal recipients are returned with `status: revoked`.',
    },
    {
      field: 'timeline',
      status: 'Required',
      detail: 'Includes the appended `document.revoked` timeline event.',
    },
  ];

  readonly revokeDocumentErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned when `reason` exceeds the supported maximum length or the request body is invalid.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Document not found',
      detail: 'Returned when the document does not exist, is deleted, or is not owned by the API key owner.',
    },
    {
      title: '409 Cannot revoke document',
      detail: 'Returned when the document is already in a terminal status such as `completed`, `declined`, `revoked`, or `expired`.',
    },
  ];

  readonly sendFromTemplateChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `POST /api/v1/public/documents/send-from-template`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Request body',
      detail: 'Send JSON with a required `templateId`, one recipient assignment for each template role, and optional override fields.',
    },
    {
      title: 'Inheritance',
      detail: 'The route inherits the template PDF, configured fields, and saved defaults unless you provide supported overrides.',
    },
    {
      title: 'Branding',
      detail: 'Set `documentSettings.brandId` to apply your saved brand to signature-request emails and document signing pages.',
    },
    {
      title: 'Send on behalf of',
      detail: 'Set `onBehalfOf` to a verified sender-identity email when the document should be sent on behalf of another approved sender.',
    },
    {
      title: 'Send behavior',
      detail: 'Omit `sendAt` to send immediately. Provide `sendAt` to create a scheduled send.',
    },
    {
      title: 'Field requirement',
      detail:
        'The template must already contain configured fields, including inherited `editable_date` fields when present. This route sends directly and does not return `configureFieldsUrl`.',
    },
  ];

  readonly sendFromTemplateParams: GuideFieldItem[] = [
    {
      field: 'templateId',
      status: 'Required',
      detail: 'Stored template id owned by the API-key owner.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
    {
      field: 'onBehalfOf',
      status: 'Optional',
      detail: 'Verified sender-identity email owned by the API-key owner. When provided, document emails use this sender identity instead of the owner account identity.',
    },
    {
      field: 'recipients[]',
      status: 'Required',
      detail: 'One assignment per template role. Each item must include `roleId`, `name`, and `email`.',
    },
    {
      field: 'document',
      status: 'Optional',
      detail: 'Optional title or message override. When omitted, the saved template document details are used.',
    },
    {
      field: 'ccEmails',
      status: 'Optional',
      detail: 'Optional CC override. When omitted, the template CC list is used.',
    },
    {
      field: 'tags',
      status: 'Optional',
      detail: 'Optional tag override. When omitted, the template tags are used.',
    },
    {
      field: 'documentSettings',
      status: 'Optional',
      detail: 'Optional document-setting override merged with the template settings. Use it to override the template brand or other send settings.',
    },
    {
      field: 'documentSettings.brandId',
      status: 'Optional',
      detail: 'Owned brand identifier applied to signature-request emails and document signing pages. Obtain it from the Brands API or the web app.',
    },
    {
      field: 'documentSettings.allowPrintAndSign',
      status: 'Optional',
      detail: 'Overrides the template `allowPrintAndSign` setting when included.',
    },
    {
      field: 'signingOrderEnabled',
      status: 'Optional',
      detail: 'Optional signing-order override. When omitted, the template signing-order setting is used.',
    },
    {
      field: 'sendAt',
      status: 'Optional',
      detail: 'Optional ISO datetime for scheduled send. Omit it to queue the document immediately.',
    },
  ];

  readonly sendFromTemplateResponseFields: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Created document id.',
    },
    {
      field: 'status',
      status: 'Required',
      detail: 'Send outcome. Immediate sends return `queued`; scheduled sends return `scheduled`.',
    },
    {
      field: 'scheduledFor',
      status: 'Optional',
      detail: 'Scheduled send time when `sendAt` is used.',
    },
    {
      field: 'sourceType',
      status: 'Required',
      detail: 'Returned as `template` for this route.',
    },
    {
      field: 'sourceTemplateId',
      status: 'Required',
      detail: 'Template id used to create the sent document.',
    },
    {
      field: 'recipients',
      status: 'Required',
      detail: 'Resolved recipient list with `roleName` and `order` derived from the template roles.',
    },
    {
      field: 'document',
      status: 'Required',
      detail: 'Effective document title and message after template inheritance and request overrides.',
    },
    {
      field: 'documentSettings',
      status: 'Required',
      detail: 'Effective document settings after template inheritance and request overrides.',
    },
    {
      field: 'documentSettings.brandId',
      status: 'Optional',
      detail: 'Resolved brand identifier used for the sent document email and signing experience.',
    },
  ];

  readonly sendFromTemplateErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned for unknown `roleId` values, missing role assignments, duplicate role assignments, invalid recipient emails, templates without fields, invalid override `brandId`, or invalid `sendAt` scheduling windows.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Template not found',
      detail: 'Returned when `templateId` is missing, deleted, or not owned by the API-key owner.',
    },
    {
      title: '404 Sender identity not found',
      detail: 'Returned when `onBehalfOf` does not match an owned sender identity.',
    },
    {
      title: '409 Sender identity must be verified before send-on-behalf-of',
      detail: 'Returned when `onBehalfOf` points to a sender identity that is still pending or has been denied.',
    },
  ];

  readonly downloadDocumentChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `GET /api/v1/public/documents/{documentId}/download`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Path parameter',
      detail: '`documentId` is required and must belong to the authenticated API-key owner.',
    },
    {
      title: 'Response type',
      detail: 'The endpoint returns raw PDF bytes with `Content-Type: application/pdf`.',
    },
    {
      title: 'Availability',
      detail: 'Only completed documents with a generated final signed PDF can be downloaded.',
    },
    {
      title: 'No fallback',
      detail: 'This route returns the final signed PDF only. It does not fall back to the source PDF.',
    },
  ];

  readonly downloadDocumentParams: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted document id to download.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly downloadDocumentResponseFields: GuideFieldItem[] = [
    {
      field: 'Content-Type',
      status: 'Required',
      detail: 'Returned as `application/pdf`.',
    },
    {
      field: 'Content-Length',
      status: 'Required',
      detail: 'Byte length of the final signed PDF response body.',
    },
    {
      field: 'Content-Disposition',
      status: 'Required',
      detail: 'Attachment header using the stored final signed filename.',
    },
    {
      field: 'Response body',
      status: 'Required',
      detail: 'Raw final signed PDF bytes for the completed document.',
    },
  ];

  readonly downloadDocumentErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Document not found',
      detail: 'Returned when the document does not exist, is deleted, or is not owned by the API key owner.',
    },
    {
      title: '409 Final signed PDF is not available yet',
      detail: 'Returned when the document is not completed yet or final PDF generation has not finished.',
    },
  ];

  readonly deleteDocumentChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `DELETE /api/v1/public/documents/{documentId}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Delete mode',
      detail: 'This route performs a soft delete by moving the document to trash with `deletedAt`.',
    },
    {
      title: 'Allowed statuses',
      detail: 'Only `draft`, `completed`, and `expired` documents can be deleted through the public API.',
    },
    {
      title: 'Request body',
      detail: 'No request body is required for this endpoint.',
    },
    {
      title: 'No permanent delete',
      detail: 'This route does not remove stored files or permanently erase the document record.',
    },
  ];

  readonly deleteDocumentParams: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted document id to move to trash.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly deleteDocumentResponseNotes: GuideListItem[] = [
    {
      title: '204 No Content',
      detail: 'Returned after the document is successfully soft-deleted.',
    },
    {
      title: 'Trash behavior',
      detail: 'Deleted documents are excluded from active document queries because `deletedAt` is set.',
    },
  ];

  readonly deleteDocumentErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Document not found',
      detail: 'Returned when the document does not exist, is already deleted, or is not owned by the API key owner.',
    },
    {
      title: '409 Only draft/completed/expired documents can be deleted',
      detail: 'Returned when the document is in an active or otherwise non-eligible status such as `waiting_for_others`, `scheduled`, `needs_attention`, `declined`, or `revoked`.',
    },
  ];

  readonly refreshConfigureUrlChecklist: GuideListItem[] = [
    {
      title: 'Purpose',
      detail: 'Request a fresh `configureFieldsUrl` when you need to reopen or re-embed the configure flow later.',
    },
    {
      title: 'Authentication',
      detail: 'Send `Authorization: Bearer <api-key>` with the API key that owns the document.',
    },
    {
      title: 'Path parameter',
      detail: '`documentId` is required in the URL path and must belong to the authenticated API key owner.',
    },
    {
      title: 'Request body',
      detail: 'No request body is required. Clients may send an empty JSON object, but no payload fields are used.',
    },
  ];

  readonly refreshConfigureUrlParams: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted document id to refresh the configure URL for.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly refreshConfigureUrlResponseFields: GuideFieldItem[] = [
    {
      field: 'documentId',
      status: 'Required',
      detail: 'Resolved owned document id returned by the API.',
    },
    {
      field: 'status',
      status: 'Required',
      detail: 'Current document status at the time the refresh request is processed.',
    },
    {
      field: 'canEditFields',
      status: 'Required',
      detail: 'Returns `true` only when the document is still in `draft`.',
    },
    {
      field: 'configureFieldsUrl',
      status: 'Required',
      detail: 'New public configure URL for opening the embedded configure flow.',
    },
  ];

  readonly refreshConfigureUrlErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Document not found',
      detail: 'Returned when the document does not exist, is deleted, or is not owned by the API key owner.',
    },
  ];

  readonly createTemplateChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `POST /api/v1/public/templates`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'File upload',
      detail: 'Attach the source PDF in the `file` multipart field.',
    },
    {
      title: 'Payload field',
      detail: 'Send template setup data in the `payload` JSON string, including roles, document details, and settings.',
    },
    {
      title: 'Response',
      detail: 'The API returns a `templateId` and a `configureFieldsUrl` for the embedded field configuration step.',
    },
  ];

  readonly createTemplateRequestParts: GuideFieldItem[] = [
    {
      field: 'file',
      status: 'Required',
      detail: 'Multipart file part. The uploaded source file must be a PDF.',
    },
    {
      field: 'payload',
      status: 'Required',
      detail: 'Multipart text part. Send a JSON string containing template setup data.',
    },
  ];

  readonly createTemplatePayloadFields: GuideFieldItem[] = [
    {
      field: 'name',
      status: 'Required',
      detail: 'Template display name.',
    },
    {
      field: 'description',
      status: 'Optional',
      detail: 'Optional template description.',
    },
    {
      field: 'roles',
      status: 'Required',
      detail: 'Array of template roles. At least one role is required, and role ids must be unique.',
    },
    {
      field: 'fields',
      status: 'Optional',
      detail: 'Optional field array. Defaults to `[]` for public create. When provided, each field must reference a known role and stay within the uploaded PDF page count.',
    },
    {
      field: 'signingOrderEnabled',
      status: 'Required',
      detail: 'Boolean flag controlling signing-order behavior.',
    },
    {
      field: 'ccEmails',
      status: 'Optional',
      detail: 'Optional CC email array. Defaults to an empty array when omitted.',
    },
    {
      field: 'document',
      status: 'Required',
      detail: 'Template document details such as `title` and optional `message`.',
    },
    {
      field: 'tags',
      status: 'Optional',
      detail: 'Optional tag array. Defaults to an empty array when omitted.',
    },
    {
      field: 'usageSettings',
      status: 'Required',
      detail: 'Required template usage permissions and behavior settings.',
    },
    {
      field: 'documentSettings',
      status: 'Required',
      detail: 'Required document settings. If `brandId` is provided, it must belong to the API-key owner.',
    },
  ];

  readonly updateTemplateChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `PATCH /api/v1/public/templates/{templateId}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Request body',
      detail: 'Send a partial JSON body with only the template fields you want to change.',
    },
    {
      title: 'Ownership',
      detail: 'The template must belong to the authenticated API key owner.',
    },
    {
      title: 'File support',
      detail: 'Source PDF replacement is not supported in v1. `sourceFile` is rejected if provided.',
    },
  ];

  readonly updateTemplateParams: GuideFieldItem[] = [
    {
      field: 'templateId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted template id to update.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly updateTemplateFields: GuideFieldItem[] = [
    {
      field: 'name',
      status: 'Optional',
      detail: 'Template display name.',
    },
    {
      field: 'description',
      status: 'Optional',
      detail: 'Template description text.',
    },
    {
      field: 'roles',
      status: 'Optional',
      detail: 'Template roles. If updated, role-to-field mapping must remain valid.',
    },
    {
      field: 'fields',
      status: 'Optional',
      detail: 'Template fields. Pages must stay within the stored PDF page count.',
    },
    {
      field: 'signingOrderEnabled',
      status: 'Optional',
      detail: 'Toggle signing order behavior.',
    },
    {
      field: 'ccEmails',
      status: 'Optional',
      detail: 'CC email recipients for the template.',
    },
    {
      field: 'document',
      status: 'Optional',
      detail: 'Template document details such as title and message.',
    },
    {
      field: 'tags',
      status: 'Optional',
      detail: 'Template tags.',
    },
    {
      field: 'usageSettings',
      status: 'Optional',
      detail: 'Template usage permissions and behavior settings.',
    },
    {
      field: 'documentSettings',
      status: 'Optional',
      detail: 'Template document settings. `brandId` must belong to the API-key owner if provided.',
    },
    {
      field: 'sourceFile',
      status: 'Optional',
      detail: 'Not supported in v1. File replacement requests are rejected.',
    },
  ];

  readonly updateTemplateResponseFields: GuideFieldItem[] = [
    {
      field: 'templateId',
      status: 'Required',
      detail: 'Resolved owned template id returned by the API.',
    },
    {
      field: 'name',
      status: 'Required',
      detail: 'Current template name after the update.',
    },
    {
      field: 'description',
      status: 'Optional',
      detail: 'Current template description after the update.',
    },
    {
      field: 'sourceFile',
      status: 'Required',
      detail: 'Stored source PDF metadata. The source file remains unchanged in v1.',
    },
    {
      field: 'updatedAt',
      status: 'Required',
      detail: 'Timestamp of the latest template update.',
    },
  ];

  readonly updateTemplateErrorNotes: GuideListItem[] = [
    {
      title: '400 Validation failed',
      detail: 'Returned for invalid roles, invalid fields, page bounds errors, unsupported keys such as `sourceFile`, or unowned `brandId`.',
    },
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Template not found',
      detail: 'Returned when the template does not exist, is deleted, or is not owned by the API key owner.',
    },
  ];

  readonly templateDetailsChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `GET /api/v1/public/templates/{templateId}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Ownership',
      detail: 'The template must belong to the authenticated API key owner and must not be deleted.',
    },
    {
      title: 'Response',
      detail: 'The API returns the full saved template definition, including title, description, roles, fields, and related settings.',
    },
  ];

  readonly templateDetailsParams: GuideFieldItem[] = [
    {
      field: 'templateId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted template id to fetch.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly templateDetailsResponseFields: GuideFieldItem[] = [
    {
      field: 'templateId',
      status: 'Required',
      detail: 'Resolved owned template id returned by the API.',
    },
    {
      field: 'name',
      status: 'Required',
      detail: 'Template display name.',
    },
    {
      field: 'description',
      status: 'Optional',
      detail: 'Template description text when present.',
    },
    {
      field: 'roles',
      status: 'Required',
      detail: 'Full saved role definitions for the template.',
    },
    {
      field: 'fields',
      status: 'Required',
      detail: 'Full saved form field definitions, including assigned roles and field settings.',
    },
    {
      field: 'document',
      status: 'Required',
      detail: 'Template document details such as title and optional message.',
    },
    {
      field: 'usageSettings',
      status: 'Required',
      detail: 'Template usage permissions and behavior settings.',
    },
    {
      field: 'documentSettings',
      status: 'Required',
      detail: 'Template document settings such as `allowPrintAndSign` and resolved brand information.',
    },
    {
      field: 'createdAt',
      status: 'Required',
      detail: 'Timestamp when the template was created.',
    },
    {
      field: 'updatedAt',
      status: 'Required',
      detail: 'Timestamp of the latest template update.',
    },
  ];

  readonly templateDetailsErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Template not found',
      detail: 'Returned when the template does not exist, is deleted, or is not owned by the API key owner.',
    },
  ];

  readonly deleteTemplateChecklist: GuideListItem[] = [
    {
      title: 'Endpoint',
      detail: 'Call `DELETE /api/v1/public/templates/{templateId}`.',
    },
    {
      title: 'Authentication',
      detail: 'Send the API key in the `Authorization: Bearer <api-key>` header.',
    },
    {
      title: 'Ownership',
      detail: 'The template must belong to the authenticated API key owner and must not already be deleted.',
    },
    {
      title: 'Request body',
      detail: 'No request body is required. Clients may send an empty JSON object, but no payload fields are used.',
    },
  ];

  readonly deleteTemplateParams: GuideFieldItem[] = [
    {
      field: 'templateId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted template id to delete.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly deleteTemplateResponseNotes: GuideListItem[] = [
    {
      title: '204 No Content',
      detail: 'Returned after the template is soft-deleted successfully.',
    },
    {
      title: 'Soft delete',
      detail: 'The route sets `deletedAt` and removes the template from active public lookups instead of hard-deleting it.',
    },
    {
      title: 'Webhook',
      detail: 'A successful delete emits the existing `template.deleted` webhook event for the owning account.',
    },
  ];

  readonly deleteTemplateErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Template not found',
      detail: 'Returned when the template does not exist, is already deleted, or is not owned by the API key owner.',
    },
  ];

  readonly templateEmbedGuideItems: GuideListItem[] = [
    {
      title: 'Iframe URL',
      detail: 'Use the returned `configureFieldsUrl` as the iframe source for the embedded template field editor.',
    },
    {
      title: 'Editable state',
      detail: 'Template configure URLs stay editable while the template exists and is not deleted.',
    },
    {
      title: 'Repeat saves',
      detail: 'The iframe can be reopened and saved multiple times through the public template config token flow.',
    },
    {
      title: 'Save event',
      detail: 'A successful save posts `template-config.saved` to the parent window for iframe integrations.',
    },
  ];

  readonly refreshTemplateConfigureUrlChecklist: GuideListItem[] = [
    {
      title: 'Purpose',
      detail: 'Request a fresh `configureFieldsUrl` when you need to reopen or re-embed template field configuration later.',
    },
    {
      title: 'Authentication',
      detail: 'Send `Authorization: Bearer <api-key>` with the API key that owns the template.',
    },
    {
      title: 'Path parameter',
      detail: '`templateId` is required in the URL path and must belong to the authenticated API key owner.',
    },
    {
      title: 'Request body',
      detail: 'No request body is required by the backend for this endpoint.',
    },
  ];

  readonly refreshTemplateConfigureUrlParams: GuideFieldItem[] = [
    {
      field: 'templateId',
      status: 'Required',
      detail: 'Path parameter. The owned, non-deleted template id to refresh the configure URL for.',
    },
    {
      field: 'Authorization',
      status: 'Required',
      detail: 'Request header in the format `Bearer <api-key>`.',
    },
  ];

  readonly refreshTemplateConfigureUrlResponseFields: GuideFieldItem[] = [
    {
      field: 'templateId',
      status: 'Required',
      detail: 'Resolved owned template id returned by the API.',
    },
    {
      field: 'configureFieldsUrl',
      status: 'Required',
      detail: 'New public configure URL for opening the embedded template field editor.',
    },
  ];

  readonly refreshTemplateConfigureUrlErrorNotes: GuideListItem[] = [
    {
      title: '401 Unauthorized',
      detail: 'Returned when the API key is missing, malformed, invalid, or expired.',
    },
    {
      title: '404 Template not found',
      detail: 'Returned when the template does not exist, is deleted, or is not owned by the API key owner.',
    },
  ];

  readonly topLevelFields: GuideFieldItem[] = [
    {
      field: 'file',
      status: 'Required',
      detail: 'Multipart PDF file upload. Only PDF is accepted.',
    },
    {
      field: 'payload',
      status: 'Required',
      detail: 'JSON string that contains the document setup data.',
    },
    {
      field: 'payload.recipients',
      status: 'Required',
      detail: 'At least one recipient is required.',
    },
    {
      field: 'payload.document',
      status: 'Required',
      detail: 'Contains the document title and optional message.',
    },
    {
      field: 'payload.documentSettings',
      status: 'Required',
      detail: 'Contains document behavior settings.',
    },
    {
      field: 'payload.ccEmails',
      status: 'Optional',
      detail: 'Optional CC recipients array.',
    },
    {
      field: 'payload.tags',
      status: 'Optional',
      detail: 'Optional document tags array.',
    },
    {
      field: 'payload.signingOrderEnabled',
      status: 'Optional',
      detail: 'Optional flag. Defaults to false when omitted.',
    },
  ];

  readonly recipientFields: GuideFieldItem[] = [
    {
      field: 'roleName',
      status: 'Required',
      detail: 'Human-readable recipient role name.',
    },
    {
      field: 'name',
      status: 'Required',
      detail: 'Recipient full name.',
    },
    {
      field: 'email',
      status: 'Required',
      detail: 'Valid recipient email address.',
    },
    {
      field: 'order',
      status: 'Required',
      detail: 'Unique signing order integer.',
    },
    {
      field: 'id',
      status: 'Auto-generated',
      detail: 'Optional. Backend generates it if omitted.',
    },
    {
      field: 'roleId',
      status: 'Auto-generated',
      detail: 'Optional. Backend generates it if omitted.',
    },
  ];

  readonly documentFields: GuideFieldItem[] = [
    {
      field: 'title',
      status: 'Required',
      detail: 'Document title shown in the signing workflow.',
    },
    {
      field: 'message',
      status: 'Optional',
      detail: 'Optional note sent with the document.',
    },
  ];

  readonly documentSettingsFields: GuideFieldItem[] = [
    {
      field: 'allowPrintAndSign',
      status: 'Required',
      detail: 'Controls whether print-and-sign is allowed.',
    },
    {
      field: 'brandId',
      status: 'Optional',
      detail: 'Optional selected brand identifier.',
    },
    {
      field: 'brandName',
      status: 'Optional',
      detail: 'Optional brand display name.',
    },
    {
      field: 'brand',
      status: 'Optional',
      detail: 'Optional legacy brand label.',
    },
  ];

  readonly requiredPayloadHighlights: GuideListItem[] = [
    {
      title: 'Required payload keys',
      detail: '`recipients`, `document`, and `documentSettings` must exist inside `payload`.',
    },
    {
      title: 'Required recipient fields',
      detail: 'Each recipient needs `roleName`, `name`, `email`, and `order`.',
    },
    {
      title: 'Required nested values',
      detail: '`document.title` and `documentSettings.allowPrintAndSign` are mandatory.',
    },
  ];

  readonly endpointExample = `POST /api/v1/public/documents
Authorization: Bearer YOUR_API_KEY
Content-Type: multipart/form-data`;

  readonly refreshEndpointExample = `POST /api/v1/public/documents/{documentId}/configure-fields-url
Authorization: Bearer YOUR_API_KEY`;

  readonly refreshRequestExample = `POST /api/v1/public/documents/{documentId}/configure-fields-url
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json

{} // Optional. No request body is required.`;

  readonly requestExample = `{
  "recipients": [
    {
      "roleName": "Employee", // Required
      "name": "Jane Doe", // Required
      "email": "jane@example.com", // Required
      "order": 1 // Required
    }
  ],
  "ccEmails": ["finance@example.com"], // Optional
  "document": {
    "title": "Offer Letter", // Required
    "message": "Please review and sign" // Optional
  },
  "tags": ["offer"], // Optional
  "documentSettings": {
    "allowPrintAndSign": false // Required
  },
  "signingOrderEnabled": false // Optional
}`;

  readonly responseExample = `{
  "documentId": "6871f27d1f95d3e3a3f7a123",
  "status": "draft",
  "expiresAt": "2026-08-06T10:30:00.000Z",
  "sourceFile": {
    "fileId": "c7d7d64b-62ef-43ef-93ae-4c4ce5e11d6f",
    "originalName": "offer.pdf",
    "pageCount": 1
  },
  "configureFieldsUrl": "https://app.example.com/developers/api/configure-fields/<token>"
}`;

  readonly updateDocumentEndpointExample = `PATCH /api/v1/public/documents/{documentId}
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`;

  readonly updateDocumentRequestExample = `{
  "document": {
    "title": "Updated Offer Letter",
    "message": "Updated message"
  },
  "tags": ["updated"],
  "ccEmails": ["updated@example.com"],
  "signingOrderEnabled": true
}`;

  readonly updateDocumentResponseExample = `{
  "documentId": "6871f27d1f95d3e3a3f7a123",
  "status": "draft",
  "expiresAt": "2026-08-06T10:30:00.000Z",
  "sourceFile": {
    "fileId": "c7d7d64b-62ef-43ef-93ae-4c4ce5e11d6f",
    "originalName": "offer.pdf",
    "pageCount": 1
  },
  "recipients": [
    {
      "id": "recipient-1",
      "roleId": "role-1",
      "roleName": "Employee",
      "name": "Jane Doe",
      "email": "jane@example.com",
      "order": 1,
      "status": "pending"
    }
  ],
  "ccEmails": ["updated@example.com"],
  "document": {
    "title": "Updated Offer Letter",
    "message": "Updated message"
  },
  "tags": ["updated"],
  "documentSettings": {
    "allowPrintAndSign": false
  },
  "signingOrderEnabled": true,
  "updatedAt": "2026-08-06T10:30:00.000Z",
  "configureFieldsUrl": "https://app.example.com/developers/api/configure-fields/<token>"
}`;

  readonly documentDetailsEndpointExample = `GET /api/v1/public/documents/{documentId}
Authorization: Bearer YOUR_API_KEY`;

  readonly documentDetailsResponseExample = `{
  "documentId": "6871f27d1f95d3e3a3f7a123",
  "status": "waiting_for_others",
  "statusReason": null,
  "sourceType": "upload",
  "signingOrderEnabled": false,
  "document": {
    "title": "Offer Letter",
    "message": "Please review and sign"
  },
  "documentSettings": {
    "brandId": "brand_8d0f3a6e31b2",
    "brandName": "Acme Brand",
    "allowPrintAndSign": false
  },
  "sourceFile": {
    "fileId": "c7d7d64b-62ef-43ef-93ae-4c4ce5e11d6f",
    "originalName": "offer.pdf",
    "pageCount": 1
  },
  "recipients": [
    {
      "id": "recipient-1",
      "roleId": "role-1",
      "roleName": "Employee",
      "name": "Jane Doe",
      "email": "jane@example.com",
      "order": 1,
      "status": "viewed",
      "invitedAt": "2026-08-06T10:31:00.000Z",
      "viewedAt": "2026-08-06T10:33:00.000Z"
    }
  ],
  "timeline": [
    {
      "event": "document.sent",
      "message": "Document queued from public API"
    }
  ],
  "submissions": [],
  "finalSignedFile": null
}`;

  readonly sendRemindersEndpointExample = `POST /api/v1/public/documents/{documentId}/remind
Authorization: Bearer YOUR_API_KEY`;

  readonly sendRemindersResponseExample = `{
  "queuedRecipientIds": [
    "recipient-1",
    "recipient-2"
  ],
  "document": {
    "documentId": "6871f27d1f95d3e3a3f7a123",
    "status": "waiting_for_others",
    "statusReason": null,
    "sourceType": "upload",
    "signingOrderEnabled": false,
    "document": {
      "title": "Offer Letter",
      "message": "Please review and sign"
    },
    "documentSettings": {
      "brandId": "brand_8d0f3a6e31b2",
      "brandName": "Acme Brand",
      "allowPrintAndSign": false
    },
    "sourceFile": {
      "fileId": "c7d7d64b-62ef-43ef-93ae-4c4ce5e11d6f",
      "originalName": "offer.pdf",
      "pageCount": 1
    },
    "recipients": [
      {
        "id": "recipient-1",
        "roleId": "role-1",
        "roleName": "Employee",
        "name": "Jane Doe",
        "email": "jane@example.com",
        "order": 1,
        "status": "invited",
        "invitedAt": "2026-08-06T10:31:00.000Z"
      },
      {
        "id": "recipient-2",
        "roleId": "role-2",
        "roleName": "Manager",
        "name": "John Doe",
        "email": "john@example.com",
        "order": 2,
        "status": "viewed",
        "invitedAt": "2026-08-06T10:32:00.000Z",
        "viewedAt": "2026-08-06T10:35:00.000Z"
      }
    ],
    "timeline": [
      {
        "event": "document.sent",
        "message": "Document queued from public API"
      },
      {
        "event": "document.reminders_queued",
        "message": "Queued reminder emails for 2 recipient(s)"
      }
    ],
    "submissions": [],
    "finalSignedFile": null
  }
}`;

  readonly revokeDocumentEndpointExample = `POST /api/v1/public/documents/{documentId}/revoke
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`;

  readonly revokeDocumentRequestExample = `{
  "reason": "Please wait for the corrected version."
}`;

  readonly revokeDocumentResponseExample = `{
  "documentId": "6871f27d1f95d3e3a3f7a123",
  "status": "revoked",
  "statusReason": "Please wait for the corrected version.",
  "sourceType": "upload",
  "signingOrderEnabled": false,
  "document": {
    "title": "Offer Letter",
    "message": "Please review and sign"
  },
  "documentSettings": {
    "brandId": "brand_8d0f3a6e31b2",
    "brandName": "Acme Brand",
    "allowPrintAndSign": false
  },
  "revokedAt": "2026-07-27T11:30:00.000Z",
  "sourceFile": {
    "fileId": "c7d7d64b-62ef-43ef-93ae-4c4ce5e11d6f",
    "originalName": "offer.pdf",
    "pageCount": 1
  },
  "recipients": [
    {
      "id": "recipient-1",
      "roleId": "role-1",
      "roleName": "Employee",
      "name": "Jane Doe",
      "email": "jane@example.com",
      "order": 1,
      "status": "revoked",
      "invitedAt": "2026-07-27T11:10:00.000Z"
    },
    {
      "id": "recipient-2",
      "roleId": "role-2",
      "roleName": "Manager",
      "name": "John Doe",
      "email": "john@example.com",
      "order": 2,
      "status": "completed",
      "completedAt": "2026-07-27T11:20:00.000Z"
    }
  ],
  "timeline": [
    {
      "event": "document.sent",
      "message": "Document queued from public API"
    },
    {
      "event": "document.revoked",
      "message": "Document revoked: Please wait for the corrected version."
    }
  ],
  "submissions": [],
  "finalSignedFile": null
}`;

  readonly sendFromTemplateEndpointExample = `POST /api/v1/public/documents/send-from-template
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`;

  readonly sendFromTemplateRequestExample = `{
  "templateId": "6871f27d1f95d3e3a3f7a123",
  "onBehalfOf": "verified.sender@example.com",
  "recipients": [
    {
      "roleId": "template-role-1",
      "name": "Jane Doe",
      "email": "jane@example.com"
    },
    {
      "roleId": "template-role-2",
      "name": "John Doe",
      "email": "john@example.com"
    }
  ],
  "document": {
    "title": "Offer Letter - Batch 14",
    "message": "Please review and sign."
  },
  "ccEmails": ["legal@example.com"],
  "tags": ["offer-letter"],
  "documentSettings": {
    "brandId": "brand_8d0f3a6e31b2",
    "allowPrintAndSign": false
  },
  "signingOrderEnabled": true,
  "sendAt": "2026-07-12T09:30:00.000Z"
}`;

  readonly sendFromTemplateResponseExample = `{
  "documentId": "6871f27d1f95d3e3a3f7a999",
  "status": "scheduled",
  "scheduledFor": "2026-07-12T09:30:00.000Z",
  "sourceType": "template",
  "sourceTemplateId": "6871f27d1f95d3e3a3f7a123",
  "recipients": [
    {
      "id": "recipient-1",
      "roleId": "template-role-1",
      "roleName": "Employee",
      "name": "Jane Doe",
      "email": "jane@example.com",
      "order": 1,
      "status": "pending"
    },
    {
      "id": "recipient-2",
      "roleId": "template-role-2",
      "roleName": "Manager",
      "name": "John Doe",
      "email": "john@example.com",
      "order": 2,
      "status": "pending"
    }
  ],
  "document": {
    "title": "Offer Letter - Batch 14",
    "message": "Please review and sign."
  },
  "documentSettings": {
    "allowPrintAndSign": false,
    "brandId": "brand_8d0f3a6e31b2"
  }
}`;

  readonly downloadDocumentEndpointExample = `GET /api/v1/public/documents/{documentId}/download
Authorization: Bearer YOUR_API_KEY`;

  readonly downloadDocumentResponseExample = `HTTP/1.1 200 OK
Content-Type: application/pdf
Content-Length: 284912
Content-Disposition: attachment; filename="offer-letter-signed.pdf"

%PDF-1.7
... binary PDF bytes ...`;

  readonly deleteDocumentEndpointExample = `DELETE /api/v1/public/documents/{documentId}
Authorization: Bearer YOUR_API_KEY`;

  readonly refreshResponseExample = `{
  "documentId": "6871f27d1f95d3e3a3f7a123",
  "status": "waiting_for_others",
  "canEditFields": false,
  "configureFieldsUrl": "https://app.example.com/developers/api/configure-fields/<new-token>"
}`;

  readonly templateEndpointExample = `POST /api/v1/public/templates
Authorization: Bearer YOUR_API_KEY
Content-Type: multipart/form-data`;

  readonly templateRequestExample = `{
  "name": "NDA Template",
  "description": "Reusable NDA template",
  "roles": [
    {
      "id": "role-1",
      "name": "Signer",
      "order": 1
    }
  ],
  "fields": [],
  "signingOrderEnabled": false,
  "ccEmails": ["legal@example.com"],
  "document": {
    "title": "Mutual NDA",
    "message": "Please review and sign."
  },
  "tags": ["nda"],
  "usageSettings": {
    "allowAddMoreFiles": true,
    "allowReplaceRearrangeDeleteFiles": true,
    "allowAddMoreRecipients": true,
    "allowEditDocumentMessage": true,
    "allowAddNewFields": true,
    "allowEditOrDeleteFields": true
  },
  "documentSettings": {
    "allowPrintAndSign": false
  }
}`;

  readonly templateResponseExample = `{
  "templateId": "6871f27d1f95d3e3a3f7a123",
  "status": "draft",
  "name": "NDA Template",
  "description": "Reusable NDA template",
  "sourceFile": {
    "fileId": "c7d7d64b-62ef-43ef-93ae-4c4ce5e11d6f",
    "originalName": "nda.pdf",
    "pageCount": 1
  },
  "signingOrderEnabled": false,
  "roles": [
    {
      "id": "role-1",
      "name": "Signer",
      "order": 1
    }
  ],
  "ccEmails": ["legal@example.com"],
  "fields": [],
  "document": {
    "title": "Mutual NDA",
    "message": "Please review and sign."
  },
  "tags": ["nda"],
  "usageSettings": {
    "allowAddMoreFiles": true,
    "allowReplaceRearrangeDeleteFiles": true,
    "allowAddMoreRecipients": true,
    "allowEditDocumentMessage": true,
    "allowAddNewFields": true,
    "allowEditOrDeleteFields": true
  },
  "documentSettings": {
    "allowPrintAndSign": false
  },
  "createdAt": "2026-08-06T10:20:00.000Z",
  "updatedAt": "2026-08-06T10:20:00.000Z",
  "configureFieldsUrl": "https://app.example.com/developers/api/configure-template-fields/<token>"
}`;

  readonly templateUpdateEndpointExample = `PATCH /api/v1/public/templates/{templateId}
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`;

  readonly templateUpdateRequestExample = `{
  "name": "Updated NDA Template",
  "description": "Updated description",
  "tags": ["updated", "template"],
  "document": {
    "title": "Updated NDA Document",
    "message": "Updated message"
  },
  "signingOrderEnabled": true
}`;

  readonly templateUpdateResponseExample = `{
  "templateId": "6871f27d1f95d3e3a3f7a123",
  "status": "draft",
  "name": "Updated NDA Template",
  "description": "Updated description",
  "sourceFile": {
    "fileId": "c7d7d64b-62ef-43ef-93ae-4c4ce5e11d6f",
    "originalName": "nda.pdf",
    "pageCount": 1
  },
  "signingOrderEnabled": true,
  "roles": [
    {
      "id": "role-1",
      "name": "Signer",
      "order": 1
    }
  ],
  "ccEmails": ["legal@example.com"],
  "fields": [],
  "document": {
    "title": "Updated NDA Document",
    "message": "Updated message"
  },
  "tags": ["updated", "template"],
  "usageSettings": {
    "allowAddMoreFiles": true,
    "allowReplaceRearrangeDeleteFiles": true,
    "allowAddMoreRecipients": true,
    "allowEditDocumentMessage": true,
    "allowAddNewFields": true,
    "allowEditOrDeleteFields": true
  },
  "documentSettings": {
    "allowPrintAndSign": false
  },
  "updatedAt": "2026-08-06T10:30:00.000Z"
}`;

  readonly templateDetailsEndpointExample = `GET /api/v1/public/templates/{templateId}
Authorization: Bearer YOUR_API_KEY`;

  readonly templateDetailsResponseExample = `{
  "templateId": "6871f27d1f95d3e3a3f7a123",
  "status": "created",
  "name": "NDA Template",
  "description": "Reusable NDA template",
  "sourceFile": {
    "fileId": "c7d7d64b-62ef-43ef-93ae-4c4ce5e11d6f",
    "originalName": "nda.pdf",
    "pageCount": 1
  },
  "signingOrderEnabled": false,
  "roles": [
    {
      "id": "role-1",
      "name": "Signer",
      "order": 1
    }
  ],
  "ccEmails": ["legal@example.com"],
  "fields": [
    {
      "id": "field-1",
      "fieldKey": "Signature1",
      "type": "signature",
      "role": "role-1",
      "page": 1,
      "required": true
    }
  ],
  "document": {
    "title": "Mutual NDA",
    "message": "Please review and sign."
  },
  "tags": ["nda"],
  "usageSettings": {
    "allowAddMoreFiles": true,
    "allowReplaceRearrangeDeleteFiles": true,
    "allowAddMoreRecipients": true,
    "allowEditDocumentMessage": true,
    "allowAddNewFields": true,
    "allowEditOrDeleteFields": true
  },
  "documentSettings": {
    "allowPrintAndSign": false
  },
  "createdAt": "2026-08-06T10:20:00.000Z",
  "updatedAt": "2026-08-06T10:30:00.000Z"
}`;

  readonly templateRefreshEndpointExample = `POST /api/v1/public/templates/{templateId}/configure-fields-url
Authorization: Bearer YOUR_API_KEY`;

  readonly templateRefreshRequestExample = `POST /api/v1/public/templates/{templateId}/configure-fields-url
Authorization: Bearer YOUR_API_KEY

// No request body required`;

  readonly templateRefreshResponseExample = `{
  "templateId": "6871f27d1f95d3e3a3f7a123",
  "configureFieldsUrl": "https://app.example.com/developers/api/configure-template-fields/<new-token>"
}`;

  readonly deleteTemplateEndpointExample = `DELETE /api/v1/public/templates/{templateId}
Authorization: Bearer YOUR_API_KEY`;

  readonly senderIdentityEndpointExample = `POST /api/v1/public/sender-identities
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`;

  readonly senderIdentityRequestExample = `{
  "name": "Approver User",
  "email": "approver@example.com",
  "brandId": "optional-brand-id"
}`;

  readonly senderIdentityResponseExample = `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "name": "Approver User",
  "email": "approver@example.com",
  "status": "pending",
  "createdAt": "2026-08-06T10:30:00.000Z",
  "approvedAt": null,
  "brand": "Alpha Brand",
  "language": "English (EN)"
}`;

  readonly senderIdentityUpdateEndpointExample = `PATCH /api/v1/public/sender-identities/{id}
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`;

  readonly senderIdentityUpdateRequestExample = `{
  "name": "Updated Approver User",
  "brandId": "optional-brand-id"
}`;

  readonly senderIdentityResendEndpointExample = `POST /api/v1/public/sender-identities/resend-verification?email=approver@example.com
Authorization: Bearer YOUR_API_KEY`;

  readonly senderIdentityRerequestEndpointExample = `POST /api/v1/public/sender-identities/rerequest?email=approver@example.com
Authorization: Bearer YOUR_API_KEY`;

  readonly senderIdentityDeleteEndpointExample = `DELETE /api/v1/public/sender-identities/{id}
Authorization: Bearer YOUR_API_KEY`;

  readonly brandCreateEndpointExample = `POST /api/v1/public/brands
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`;

  readonly brandListEndpointExample = `GET /api/v1/public/brands
Authorization: Bearer YOUR_API_KEY`;

  readonly brandListResponseExample = `{
  "total": 2,
  "items": [
    {
      "id": "6871f27d1f95d3e3a3f7a123",
      "brandId": "brand_8d0f3a6e31b2",
      "name": "Acme Brand",
      "isDefault": true,
      "logoDataUrl": "data:image/png;base64,aGVsbG8=",
      "emailDisplayName": "Acme Notifications",
      "emailBackgroundColor": "#edf4ff",
      "primaryButtonColor": "#0f62fe",
      "primaryButtonTextColor": "#ffffff",
      "showPlatformBranding": true,
      "createdAt": "2026-08-06T10:30:00.000Z",
      "updatedAt": "2026-08-06T10:30:00.000Z"
    },
    {
      "id": "6871f27d1f95d3e3a3f7a124",
      "brandId": "brand_9a1b2c3d4e5f",
      "name": "Sales Brand",
      "isDefault": false,
      "logoDataUrl": "data:image/png;base64,YmFzZTY0",
      "emailDisplayName": "Sales Team",
      "emailBackgroundColor": "#f7f8fa",
      "primaryButtonColor": "#111111",
      "primaryButtonTextColor": "#ffffff",
      "showPlatformBranding": false,
      "createdAt": "2026-08-07T08:15:00.000Z",
      "updatedAt": "2026-08-07T08:15:00.000Z"
    }
  ]
}`;

  readonly brandCreateRequestExample = `{
  "name": "Acme Brand",
  "isDefault": false,
  "logoDataUrl": "data:image/png;base64,aGVsbG8=",
  "emailDisplayName": "Acme Notifications",
  "emailBackgroundColor": "#edf4ff",
  "primaryButtonColor": "#0f62fe",
  "primaryButtonTextColor": "#ffffff",
  "showPlatformBranding": true
}`;

  readonly brandCreateResponseExample = `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "brandId": "brand_8d0f3a6e31b2",
  "name": "Acme Brand",
  "isDefault": true,
  "logoDataUrl": "data:image/png;base64,aGVsbG8=",
  "emailDisplayName": "Acme Notifications",
  "emailBackgroundColor": "#edf4ff",
  "primaryButtonColor": "#0f62fe",
  "primaryButtonTextColor": "#ffffff",
  "showPlatformBranding": true,
  "createdAt": "2026-08-06T10:30:00.000Z",
  "updatedAt": "2026-08-06T10:30:00.000Z"
}`;

  readonly brandUpdateEndpointExample = `PATCH /api/v1/public/brands/{brandId}
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`;

  readonly brandUpdateRequestExample = `{
  "name": "Acme Brand Updated",
  "primaryButtonColor": "#101010",
  "showPlatformBranding": false
}`;

  readonly brandUpdateResponseExample = `{
  "id": "6871f27d1f95d3e3a3f7a123",
  "brandId": "brand_8d0f3a6e31b2",
  "name": "Acme Brand Updated",
  "isDefault": true,
  "logoDataUrl": "data:image/png;base64,aGVsbG8=",
  "emailDisplayName": "Acme Notifications",
  "emailBackgroundColor": "#edf4ff",
  "primaryButtonColor": "#101010",
  "primaryButtonTextColor": "#ffffff",
  "showPlatformBranding": false,
  "createdAt": "2026-08-06T10:30:00.000Z",
  "updatedAt": "2026-08-06T11:15:00.000Z"
}`;

  readonly brandDeleteEndpointExample = `DELETE /api/v1/public/brands/{brandId}
Authorization: Bearer YOUR_API_KEY`;

  get breadcrumbs(): string[] {
    return ['API Reference', this.activeSectionLabel];
  }

  get activeSectionLabel(): string {
    if (this.activeSection === 'documents') {
      return 'Documents';
    }

    if (this.activeSection === 'templates') {
      return 'Templates';
    }

    if (this.activeSection === 'sender-identities') {
      return 'Sender Identities';
    }

    if (this.activeSection === 'brands') {
      return 'Brands';
    }

    if (this.activeSection === 'webhooks') {
      return 'Webhooks';
    }

    return 'Get API Key';
  }

  get pageSections(): PageSectionLink[] {
    if (this.activeSection === 'documents') {
      return this.documentSections;
    }

    if (this.activeSection === 'templates') {
      return this.templateSections;
    }

    if (this.activeSection === 'sender-identities') {
      return this.senderIdentitySections;
    }

    if (this.activeSection === 'brands') {
      return this.brandSections;
    }

    if (this.activeSection === 'webhooks') {
      return this.webhookSections;
    }

    return this.apiKeySections;
  }

  isTopLevelActive(section: TopLevelSectionId): boolean {
    return this.activeSection === section;
  }

  selectTopLevelSection(section: TopLevelSectionId): void {
    this.activeSection = section;

    if (section === 'documents') {
      this.documentsExpanded = true;
      this.activePageSectionId = this.activeDocumentSubSectionAnchor;
      this.scheduleScrollToAnchor(this.activeDocumentSubSectionAnchor);
      return;
    }

    if (section === 'templates') {
      this.templatesExpanded = true;
      this.activePageSectionId = this.activeTemplateSubSectionAnchor;
      this.scheduleScrollToAnchor(this.activeTemplateSubSectionAnchor);
      return;
    }

    if (section === 'sender-identities') {
      this.senderIdentitiesExpanded = true;
      this.activePageSectionId = this.activeSenderIdentitySubSectionAnchor;
      this.scheduleScrollToAnchor(this.activeSenderIdentitySubSectionAnchor);
      return;
    }

    if (section === 'brands') {
      this.brandsExpanded = true;
      this.activePageSectionId = this.activeBrandSubSectionAnchor;
      this.scheduleScrollToAnchor(this.activeBrandSubSectionAnchor);
      return;
    }

    if (section === 'webhooks') {
      this.webhooksExpanded = true;
      this.activePageSectionId = this.activeWebhookSubSectionAnchor;
      this.scheduleScrollToAnchor(this.activeWebhookSubSectionAnchor);
      return;
    }

    this.activePageSectionId = 'api-key-overview';
    this.scheduleScrollToAnchor('api-key-overview');
  }

  toggleDocumentsSection(): void {
    if (!this.isTopLevelActive('documents')) {
      this.selectTopLevelSection('documents');
      return;
    }

    this.documentsExpanded = !this.documentsExpanded;
  }

  toggleTemplatesSection(): void {
    if (!this.isTopLevelActive('templates')) {
      this.selectTopLevelSection('templates');
      return;
    }

    this.templatesExpanded = !this.templatesExpanded;
  }

  toggleSenderIdentitiesSection(): void {
    if (!this.isTopLevelActive('sender-identities')) {
      this.selectTopLevelSection('sender-identities');
      return;
    }

    this.senderIdentitiesExpanded = !this.senderIdentitiesExpanded;
  }

  toggleBrandsSection(): void {
    if (!this.isTopLevelActive('brands')) {
      this.selectTopLevelSection('brands');
      return;
    }

    this.brandsExpanded = !this.brandsExpanded;
  }

  toggleWebhooksSection(): void {
    if (!this.isTopLevelActive('webhooks')) {
      this.selectTopLevelSection('webhooks');
      return;
    }

    this.webhooksExpanded = !this.webhooksExpanded;
  }

  selectDocumentSubSection(section: DocumentSubSectionId): void {
    this.activeSection = 'documents';
    this.documentsExpanded = true;
    this.activeDocumentSubSection = section;
    this.activePageSectionId = this.activeDocumentSubSectionAnchor;
    this.scheduleScrollToAnchor(this.activeDocumentSubSectionAnchor);
  }

  isDocumentSubSectionActive(section: DocumentSubSectionId): boolean {
    return this.activeDocumentSubSection === section;
  }

  selectTemplateSubSection(section: TemplateSubSectionId): void {
    this.activeSection = 'templates';
    this.templatesExpanded = true;
    this.activeTemplateSubSection = section;
    this.activePageSectionId = this.activeTemplateSubSectionAnchor;
    this.scheduleScrollToAnchor(this.activeTemplateSubSectionAnchor);
  }

  isTemplateSubSectionActive(section: TemplateSubSectionId): boolean {
    return this.activeTemplateSubSection === section;
  }

  selectSenderIdentitySubSection(section: SenderIdentitySubSectionId): void {
    this.activeSection = 'sender-identities';
    this.senderIdentitiesExpanded = true;
    this.activeSenderIdentitySubSection = section;
    this.activePageSectionId = this.activeSenderIdentitySubSectionAnchor;
    this.scheduleScrollToAnchor(this.activeSenderIdentitySubSectionAnchor);
  }

  isSenderIdentitySubSectionActive(section: SenderIdentitySubSectionId): boolean {
    return this.activeSenderIdentitySubSection === section;
  }

  selectBrandSubSection(section: BrandSubSectionId): void {
    this.activeSection = 'brands';
    this.brandsExpanded = true;
    this.activeBrandSubSection = section;
    this.activePageSectionId = this.activeBrandSubSectionAnchor;
    this.scheduleScrollToAnchor(this.activeBrandSubSectionAnchor);
  }

  isBrandSubSectionActive(section: BrandSubSectionId): boolean {
    return this.activeBrandSubSection === section;
  }

  selectWebhookSubSection(section: WebhookSubSectionId): void {
    this.activeSection = 'webhooks';
    this.webhooksExpanded = true;
    this.activeWebhookSubSection = section;
    this.activePageSectionId = this.activeWebhookSubSectionAnchor;
    this.scheduleScrollToAnchor(this.activeWebhookSubSectionAnchor);
  }

  isWebhookSubSectionActive(section: WebhookSubSectionId): boolean {
    return this.activeWebhookSubSection === section;
  }

  selectPageSection(targetId: string): void {
    let targetTopLevelSection: TopLevelSectionId = 'get-api-key';
    if (this.documentSections.some((section) => section.targetId === targetId)) {
      targetTopLevelSection = 'documents';
    } else if (this.templateSections.some((section) => section.targetId === targetId)) {
      targetTopLevelSection = 'templates';
    } else if (this.senderIdentitySections.some((section) => section.targetId === targetId)) {
      targetTopLevelSection = 'sender-identities';
    } else if (this.brandSections.some((section) => section.targetId === targetId)) {
      targetTopLevelSection = 'brands';
    } else if (this.webhookSections.some((section) => section.targetId === targetId)) {
      targetTopLevelSection = 'webhooks';
    }

    if (targetTopLevelSection === 'documents') {
      this.activeSection = 'documents';
      this.documentsExpanded = true;

      if (targetId === 'create-document') {
        this.activeDocumentSubSection = 'create-document';
      } else if (targetId === 'update-document') {
        this.activeDocumentSubSection = 'update-document';
      } else if (targetId === 'send-reminders') {
        this.activeDocumentSubSection = 'send-reminders';
      } else if (targetId === 'revoke-document') {
        this.activeDocumentSubSection = 'revoke-document';
      } else if (targetId === 'send-document-from-template') {
        this.activeDocumentSubSection = 'send-document-from-template';
      } else if (targetId === 'download-document') {
        this.activeDocumentSubSection = 'download-document';
      } else if (targetId === 'delete-document') {
        this.activeDocumentSubSection = 'delete-document';
      } else if (targetId === 'embedded-configure-fields') {
        this.activeDocumentSubSection = 'embedded-configure-fields';
      } else if (targetId === 'refresh-configure-url') {
        this.activeDocumentSubSection = 'refresh-configure-url';
      }
    } else if (targetTopLevelSection === 'templates') {
      this.activeSection = 'templates';
      this.templatesExpanded = true;

      if (targetId === 'create-template') {
        this.activeTemplateSubSection = 'create-template';
      } else if (targetId === 'update-template') {
        this.activeTemplateSubSection = 'update-template';
      } else if (targetId === 'template-details') {
        this.activeTemplateSubSection = 'template-details';
      } else if (targetId === 'delete-template') {
        this.activeTemplateSubSection = 'delete-template';
      } else if (targetId === 'embedded-template-configure-fields') {
        this.activeTemplateSubSection = 'embedded-template-configure-fields';
      } else if (targetId === 'refresh-template-configure-url') {
        this.activeTemplateSubSection = 'refresh-template-configure-url';
      }
    } else if (targetTopLevelSection === 'sender-identities') {
      this.activeSection = 'sender-identities';
      this.senderIdentitiesExpanded = true;

      if (targetId === 'create-sender-identity') {
        this.activeSenderIdentitySubSection = 'create-sender-identity';
      } else if (targetId === 'update-sender-identity') {
        this.activeSenderIdentitySubSection = 'update-sender-identity';
      } else if (targetId === 'resend-sender-identity-invitation') {
        this.activeSenderIdentitySubSection = 'resend-sender-identity-invitation';
      } else if (targetId === 'rerequest-sender-identity-approval') {
        this.activeSenderIdentitySubSection = 'rerequest-sender-identity-approval';
      } else if (targetId === 'delete-sender-identity') {
        this.activeSenderIdentitySubSection = 'delete-sender-identity';
      }
    } else if (targetTopLevelSection === 'brands') {
      this.activeSection = 'brands';
      this.brandsExpanded = true;

      if (targetId === 'create-brand') {
        this.activeBrandSubSection = 'create-brand';
      }
    } else if (targetTopLevelSection === 'webhooks') {
      this.activeSection = 'webhooks';
      this.webhooksExpanded = true;

      if (targetId === 'webhooks-overview') {
        this.activeWebhookSubSection = 'webhooks-overview';
      } else if (targetId === 'sender-identity-events') {
        this.activeWebhookSubSection = 'sender-identity-events';
      } else if (targetId === 'template-events') {
        this.activeWebhookSubSection = 'template-events';
      } else {
        this.activeWebhookSubSection = 'document-events';
      }
    } else {
      this.activeSection = 'get-api-key';
    }

    this.activePageSectionId = targetId;
    this.scheduleScrollToAnchor(targetId);
  }

  isPageSectionActive(targetId: string): boolean {
    return this.activePageSectionId === targetId;
  }

  private get activeDocumentSubSectionAnchor(): string {
    return this.activeDocumentSubSection;
  }

  private get activeTemplateSubSectionAnchor(): string {
    return this.activeTemplateSubSection;
  }

  private get activeSenderIdentitySubSectionAnchor(): string {
    return this.activeSenderIdentitySubSection;
  }

  private get activeBrandSubSectionAnchor(): string {
    return this.activeBrandSubSection;
  }

  private get activeWebhookSubSectionAnchor(): string {
    return this.activeWebhookSubSection;
  }

  private scheduleScrollToAnchor(anchorId: string): void {
    globalThis.setTimeout(() => {
      const target = this.documentRef.getElementById(anchorId);
      target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
  }
}
